import { createAction, handleActions } from "redux-actions";
import produce from "immer";
import { takeLatest } from "redux-saga/effects";
import createRequestSaga, {
  createRequestActionType,
} from "../lib/createRequestSaga";
import * as authAPI from "../lib/api/auth";

/* 
  redux state를 다루기 위한 redux action의 이름 정의
*/
const CHANGE_FIELD = "auth/CHANGE_FIELD";
const INITIALIZE_FORM = "auth/INITIALIZE_FORM";

const [LOGIN, LOGIN_SUCCESS, LOGIN_FAILURE] =
  createRequestActionType("auth/LOGIN");
const [SET_COOKIE, SET_COOKIE_SUCCESS, SET_COOKIE_FAILURE] =
  createRequestActionType("auth/SET_COOKIE");
const [CHECK, CHECK_SUCCESS, CHECK_FAILURE] =
  createRequestActionType("auth/CHECK");
const SET_DOMAIN_NAMES = "auth/SET_DOMAIN_NAMES";

/*
 실제로 외부에서 쓰이는 액션들
 첫번째 파라미터는 액션의 이름을 뜻함(redux-saga(미들웨어)와 이름으로 연동됨).
 두번째 파라미터는 액션의 메소드를 뜻함.
 */
export const changeField = createAction(
  CHANGE_FIELD,
  ({ form, key, value }) => ({
    form,
    key,
    value,
  })
);
export const initializeForm = createAction(INITIALIZE_FORM, (form) => form);
export const login = createAction(
  LOGIN,
  ({ t_type, diag_t_id, name, grade, st_month }) => ({
    t_type,
    diag_t_id,
    name,
    grade,
    st_month,
  })
);
export const setCookie = createAction(SET_COOKIE, ({ token, is_middle }) => ({
  token,
  is_middle,
}));
export const check = createAction(CHECK);
export const setDomainNames = createAction(SET_DOMAIN_NAMES, (arr) => arr);

/*
 redux-saga: 여러 용도가 있지만 여기선 http aixos 통신을 목적으로 사용함.(통신 중, 통신 종료 상태등을 추적하려는 목적)
 action의 이름을 기반으로 위에서 만든 액션들과 연동됨.
 첫번째 파라미터는 saga 및 액션의 이름, 두번째 파라미터는 http axios 요청을 의미함.
 */
const loginSaga = createRequestSaga(LOGIN, authAPI.login);
const setCookieSaga = createRequestSaga(SET_COOKIE, authAPI.setCookie);
const checkSaga = createRequestSaga(CHECK, authAPI.check);

/*
 여러 reudx-saga들을 하나의 큰 saga로 묶어줌 + 
 takeLatest()는 해당 saga가 여러번 발생해도 마지막 saga만 동작하도록 처리(중복 방지)
*/
export function* authSaga() {
  yield takeLatest(LOGIN, loginSaga);
  yield takeLatest(SET_COOKIE, setCookieSaga);
  yield takeLatest(CHECK, checkSaga);
}

/*
 redux state의 초기 상태

 login: login 정보
 cookie: 쿠키 스토어 토큰 셋팅 성공 정보
 cookieError: 쿠키 스토어 토큰 셋팅 실패 정보
 auth: 쿠키 스토어 토큰 해독 후 유저 정보
 authError: 쿠키 스토어 유효성 검증 실패 정보
 domainNames: 학년에 따른 영역별 이름
*/
const initialState = {
  login: {
    t_type: "test",
    diag_t_id: "0",
    name: "",
    grade: "01",
    st_month: "",
  },
  cookie: null,
  cookieError: null,
  auth: null,
  authError: null,
  domainNames: [
    ["수와 연산", "수와 연산"],
    ["도형", "도형"],
    ["측정", "측정"],
    ["규칙성", "규칙성"],
    ["자료와 가능성", "자료와 가능성"],
  ],
};

/*
 auth에 해당하는 모든 액션들의 기능을 정의하고, redux state 값도 포함함
 외부에서 auth 객체를 사용함으로써 auth의 액션들을 사용할 수 있고,
 auth의 redux state 정보들도 사용이 가능함.

 saga 들이 success / failure만 있는 이유는 통신 중 상태는 loading 모듈이 따로 관리하고
 프론트 클라이언트가 실제로 필요한 상태는 success / failure 뿐이기 때문

 이렇게 만든 handleActions 객체는 reducer라고 불림
*/
const auth = handleActions(
  {
    [CHANGE_FIELD]: (state, { payload: { form, key, value } }) =>
      produce(state, (draft) => {
        draft[form][key] = value;
      }),
    [INITIALIZE_FORM]: (state, { payload: form }) => ({
      ...state,
      [form]: initialState[form],
      authError: null,
    }),
    [LOGIN_SUCCESS]: (state, { payload: auth }) => ({
      ...state,
      authError: null,
      auth,
    }),
    [LOGIN_FAILURE]: (state, { payload: error }) => ({
      ...state,
      authError: error,
    }),
    [SET_COOKIE_SUCCESS]: (state, { payload: cookie }) => ({
      ...state,
      cookieError: null,
      cookie,
    }),
    [SET_COOKIE_FAILURE]: (state, { payload: error }) => ({
      ...state,
      cookieError: error,
      cookie: null,
    }),
    [CHECK_SUCCESS]: (state, { payload: auth }) => ({
      ...state,
      auth,
      authError: null,
    }),
    [CHECK_FAILURE]: (state, { payload: error }) => ({
      ...state,
      auth: null,
      authError: error,
    }),
    [SET_DOMAIN_NAMES]: (state, { payload: arr }) => ({
      ...state,
      domainNames: arr,
    }),
  },
  initialState
);

export default auth;
